import { schema, t, type SchemaType } from "@colyseus/schema";
import { BATTLE_ROYALE, NET, PISTOL, SUPPLY } from "./config.ts";
import { DAMAGE } from "./damage.ts";
import type { RoyalePhase } from "./royale.ts";

/**
 * Replicated room state. The server owns every field; clients only read.
 * Positions are float32 (sub-millimetre precision inside the 6 km map).
 */

export const PlayerState = schema(
  {
    id: t.string().default(""),
    name: t.string().default(""),
    /** Character look code (see look.ts); "" is the classic crewmate. */
    look: t.string().default(""),
    connected: t.boolean().default(true),
    frame: t.uint8().default(0),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vy: t.float32().default(0),
    vx: t.float32().default(0),
    vz: t.float32().default(0),
    launchTicks: t.uint32().default(0),
    launchSerial: t.uint32().default(0),
    cannonLoaded: t.boolean().default(false),
    yaw: t.float32().default(0),
    pitch: t.float32().default(0),
    grounded: t.boolean().default(true),
    mode: t.uint8().default(0),
    fallStartY: t.float32().default(0),
    breath: t.float32().default(20),
    crouched: t.boolean().default(false),
    sprinting: t.boolean().default(false),
    sprintTime: t.float32().default(0),
    slideRemaining: t.float32().default(0),
    slideYaw: t.float32().default(0),
    slideCooldown: t.float32().default(0),
    crouchHeld: t.boolean().default(false),
    health: t.float32().default(100),
    dead: t.boolean().default(false),
    respawnIn: t.float32().default(0),
    /** Last input sequence the server simulated for this player. */
    ack: t.uint32().default(0),
    carrying: t.string().default(""),
    /** Crew slot, or -1 before choosing a crew. */
    crew: t.int8().default(-1),
    /** "" | "wheel" | "cannon:0".."cannon:5" | "islandCannon:<id>" | "ladder" | "telescope:<id>" | "nestScope". */
    station: t.string().default(""),
    /** "", "repair", "fill", "throw" — the bucket or repair action in progress. */
    action: t.string().default(""),
    actionProgress: t.float32().default(0),
    recoverIn: t.float32().default(0),
    /** "sword" | "shovel" | "spyglass" | "pistol", or "bucket" while holding a bucket from the ship's rack. */
    equipment: t.string().default("shovel"),
    /** The held bucket carries water (only meaningful with equipment "bucket"). */
    bucketFull: t.boolean().default(false),
    blocking: t.boolean().default(false),
    attackRemaining: t.float32().default(0),
    attackSerial: t.uint32().default(0),
    attackStep: t.uint8().default(0),
    attackDuration: t.float32().default(0),
    staggerRemaining: t.float32().default(0),
    scopeRaised: t.boolean().default(false),
    climbShip: t.int8().default(-1),
    /** SHIP_LADDERS index while climbing a ship ladder (climbShip >= 0). */
    shipLadder: t.uint8().default(0),
    /** WORLD_LADDERS index while on a tower ladder (climbShip is then -1). */
    climbLadder: t.int8().default(-1),
    /** Signed climb pace, -1 (down) … 1 (up), applied on the latest tick; 0 while hanging still. */
    climbRate: t.float32().default(0),
    climbProgress: t.float32().default(0),
    bot: t.boolean().default(false),
    pistolRounds: t.uint8().default(PISTOL.capacity),
    pistolReserve: t.uint8().default(PISTOL.maxAmmo - PISTOL.capacity),
    pistolReloadRemaining: t.float32().default(0),
    pistolRecoveryRemaining: t.float32().default(0),
    pistolAiming: t.boolean().default(false),
    pistolSerial: t.uint32().default(0),
    /** Server tick of the last accepted dig; -1 before digging. Presentation only. */
    digTick: t.float64().default(-1),
    /** Battle royale: crewless sailor who gets a ship next round. */
    queued: t.boolean().default(false),
  },
  "PlayerState",
);
export type PlayerState = SchemaType<typeof PlayerState>;

export const CannonState = schema(
  {
    owner: t.string().default(""),
    reload: t.float32().default(0),
    /** Dismounted by shot or a ram: cannot be manned until remounted (ship guns only). */
    wrecked: t.boolean().default(false),
  },
  "CannonState",
);
export type CannonState = SchemaType<typeof CannonState>;

export const LiveryState = schema(
  {
    /** Index into `LIVERY.figureheads`. */
    figurehead: t.uint8().default(0),
    /** Index into `LIVERY.emblems` (main course sail). */
    emblem: t.uint8().default(0),
    /** Index into `LIVERY.flags`. */
    flag: t.uint8().default(0),
  },
  "LiveryState",
);
export type LiveryState = SchemaType<typeof LiveryState>;

export const UpgradeState = schema(
  {
    hull: t.uint8().default(0),
    guns: t.uint8().default(0),
    hold: t.uint8().default(0),
  },
  "UpgradeState",
);
export type UpgradeState = SchemaType<typeof UpgradeState>;

export const ShipState = schema(
  {
    slot: t.uint8().default(0),
    x: t.float32().default(0),
    z: t.float32().default(0),
    heading: t.float32().default(0),
    speed: t.float32().default(0),
    rudder: t.float32().default(0),
    sail: t.uint8().default(0),
    anchored: t.boolean().default(true),
    /** 0 catted … 1 fully paid out (see `stepAnchor`). */
    anchorDepth: t.float32().default(1),
    /** The pawl is kicked off and the anchor is running out. */
    anchorFalling: t.boolean().default(false),
    docked: t.boolean().default(true),
    flooding: t.float32().default(0),
    /** Bit i set ⇒ leak slot i is open. */
    leaks: t.uint8().default(0),
    /** 2 bits per leak slot: extra hole severity above 1 (`breachSeverity`). */
    breaches: t.uint8().default(0),
    /** Bit i set ⇒ `ROPES[i]` is cut. */
    ropes: t.uint32().default(0),
    /** 10 bits per sail: 9 hole cells and a torn-away bit (`sailHoles`, `sailShredded`). */
    sailHoles: t.uint32().default(0),
    /** Helm integrity, DAMAGE.wheelHp intact … 0 smashed (no steering). */
    wheelHp: t.uint8().default(DAMAGE.wheelHp),
    wheelOwner: t.string().default(""),
    sinking: t.boolean().default(false),
    sinkIn: t.float32().default(0),
    /** Increments on every respawn so clients can reset interpolation. */
    generation: t.uint16().default(0),
    cannonballs: t.uint8().default(SUPPLY.startBalls),
    planks: t.uint8().default(SUPPLY.startPlanks),
    cannons: t.array(CannonState),
    livery: LiveryState,
    upgrades: UpgradeState,
    /** Bit i set ⇒ `SHIP_POINTS.lanterns[i]` is lit. */
    lanterns: t.uint8().default(15),
    /** Wrapping serial; clients swing the bell when it changes. */
    bellRings: t.uint8().default(0),
  },
  "ShipState",
);
export type ShipState = SchemaType<typeof ShipState>;

export const CHEST_WORLD = 0;
export const CHEST_SHIP = 1;
export const CHEST_CARRIED = 2;
export const CHEST_FLOATING = 3;

export const ChestState = schema(
  {
    id: t.string().default(""),
    loc: t.uint8().default(CHEST_WORLD),
    carrier: t.string().default(""),
    /** Crew slot of the ship when loc === CHEST_SHIP. */
    ship: t.int8().default(-1),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    yaw: t.float32().default(0),
    tier: t.uint8().default(0),
  },
  "ChestState",
);
export type ChestState = SchemaType<typeof ChestState>;

export const QuestState = schema(
  {
    /** "none" | "active" | "revealed" */
    status: t.string().default("none"),
    island: t.string().default(""),
    spot: t.uint8().default(0),
    dig: t.uint8().default(0),
    chestId: t.string().default(""),
    /** Increments per accepted quest. */
    number: t.uint16().default(0),
    kind: t.string().default("map"),
    leg: t.uint8().default(0),
    legs: t.uint8().default(1),
  },
  "QuestState",
);
export type QuestState = SchemaType<typeof QuestState>;

export const VoteState = schema(
  {
    active: t.boolean().default(false),
    initiator: t.string().default(""),
    yes: t.array("string"),
    endsIn: t.float32().default(0),
    cooldown: t.float32().default(0),
  },
  "VoteState",
);
export type VoteState = SchemaType<typeof VoteState>;

export const DigMark = schema(
  {
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
  },
  "DigMark",
);
export type DigMark = SchemaType<typeof DigMark>;

export const CrewState = schema(
  {
    slot: t.uint8().default(0),
    name: t.string().default(""),
    color: t.string().default(""),
    ship: ShipState,
    quest: QuestState,
    vote: VoteState,
    gold: t.uint32().default(0),
    questsCompleted: t.uint16().default(0),
    titleIndex: t.uint8().default(0),
    npc: t.boolean().default(false),
    lanternDiscoveries: t.uint8().default(0),
    lanternClues: t.uint8().default(0),
    lanternSolved: t.boolean().default(false),
  },
  "CrewState",
);
export type CrewState = SchemaType<typeof CrewState>;

/** A cannonball in flight; clients evaluate its arc from launch values. */
export const BallState = schema(
  {
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vx: t.float32().default(0),
    vy: t.float32().default(0),
    vz: t.float32().default(0),
    /** Server tick of launch. */
    tick: t.uint32().default(0),
    /** Crew slot of the ship that fired it. */
    ship: t.int8().default(-1),
  },
  "BallState",
);
export type BallState = SchemaType<typeof BallState>;

/** Room-wide mechanism and physical reward claims; crew notes live separately. */
export const LanternState = schema(
  {
    bearing: t.uint8().default(2),
    colour: t.uint8().default(1),
    revealed: t.boolean().default(false),
    cargoTaken: t.boolean().default(false),
    cacheTaken: t.boolean().default(false),
    cargoChestId: t.string().default(""),
    cacheChestId: t.string().default(""),
    restockIn: t.float32().default(0),
    supplyCooldown: t.float32().default(0),
  },
  "LanternState",
);
export type LanternState = SchemaType<typeof LanternState>;

export const LootState = schema(
  {
    id: t.string().default(""),
    /** LootKind: "balls" | "planks". */
    kind: t.string().default("balls"),
    x: t.float32().default(0),
    z: t.float32().default(0),
  },
  "LootState",
);
export type LootState = SchemaType<typeof LootState>;

export const RoyaleState = schema(
  {
    enabled: t.boolean().default(false),
    /** RoyalePhase: "waiting" | "countdown" | "active" | "ended". */
    phase: t.string().default("waiting"),
    phaseIn: t.float32().default(0),
    elapsed: t.float32().default(0),
    round: t.uint16().default(0),
    radius: t.float32().default(BATTLE_ROYALE.ringStart),
    alive: t.uint8().default(0),
    treasureId: t.string().default(""),
    treasureIn: t.float32().default(0),
    holder: t.int8().default(-1),
    holdTime: t.float32().default(0),
    winner: t.string().default(""),
    winnerSlot: t.int8().default(-1),
    /** "" | "last" | "treasure" | "none". */
    winReason: t.string().default(""),
  },
  "RoyaleState",
);
export type RoyaleState = SchemaType<typeof RoyaleState>;

export const GameState = schema(
  {
    capacity: t.uint8().default(NET.maxPlayers),
    tick: t.uint32().default(0),
    players: t.map(PlayerState),
    /** Crews keyed by String(slot). */
    crews: t.map(CrewState),
    chests: t.map(ChestState),
    balls: t.map(BallState),
    islandCannons: t.map(CannonState),
    lantern: LanternState,
    /** Refill cooldown (seconds) per supply barrel id. */
    barrels: t.array("float32"),
    /** Open flag per GATES index. */
    gates: t.array("boolean"),
    /** Respawn cooldown (seconds) per LOOT_CRATES index; 0 = intact. */
    crates: t.array("float32"),
    digs: t.array(DigMark),
    paused: t.boolean().default(false),
    /** Seeds the room's weather (`weatherAt` over `tick`). */
    weatherSeed: t.uint32().default(0),
    royale: RoyaleState,
    /** Battle royale floating loot keyed by id. */
    loot: t.map(LootState),
  },
  "GameState",
);
export type GameState = SchemaType<typeof GameState>;

export interface SeaStatus {
  humans: number;
  reserved: number;
  crewBots: number;
  npcSailors: number;
  population: number;
  capacity: number;
  /** Battle royale round snapshot; only the royale room reports it. */
  royale?: RoyaleStatus;
}

export interface RoyaleStatus {
  phase: RoyalePhase;
  /** Whole seconds left in the phase. */
  phaseIn: number;
  round: number;
  alive: number;
}

/** Count occupied sailor seats on HTTP requests or schema patches, not each frame. */
export function getSeaStatus(state: GameState): SeaStatus {
  let humans = 0, reserved = 0, crewBots = 0, npcSailors = 0;
  state.players.forEach((p) => {
    if (!p.bot) {
      if (p.connected) humans++;
      else reserved++;
    } else if (state.crews.get(String(p.crew))?.npc) npcSailors++;
    else crewBots++;
  });
  return { humans, reserved, crewBots, npcSailors, population: humans + reserved + crewBots + npcSailors, capacity: state.capacity };
}

export function getRoyaleStatus(state: GameState): RoyaleStatus {
  const r = state.royale;
  return { phase: r.phase as RoyalePhase, phaseIn: Math.ceil(r.phaseIn), round: r.round, alive: r.alive };
}
